import React, { useEffect, useState } from 'react';
import { bookingService, type PricingConfig } from '../../services/booking';

const PricingControl: React.FC = () => {
    const [configs, setConfigs] = useState<PricingConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<PricingConfig>>({});

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        try {
            const data = await bookingService.getPricingConfigs();
            setConfigs(data);
        } catch (err: any) {
            console.error('Error fetching pricing configs:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (config: PricingConfig) => {
        setEditingId(config.id);
        setEditForm(config);
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditForm({});
    };

    const handleSave = async (id: string) => {
        try {
            setLoading(true);
            await bookingService.updatePricingConfig(id, editForm);
            await fetchConfigs();
            setEditingId(null);
            setEditForm({});
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const renderCategorySection = (title: string, category: string, icon: string, color: string) => {
        const config = configs.find(c => c.service_type === category);
        if (!config) return null;

        const isGlobal = category === 'global';

        return (
            <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
                <div className={`px-6 py-4 border-b border-gray-200 bg-${color}-50 flex items-center`}>
                    <i className={`${icon} text-2xl text-${color}-600 mr-3`}></i>
                    <h3 className="text-lg font-medium text-gray-900">{title}</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {isGlobal ? 'USD to NGN Rate' : 'Markup %'}
                                </th>
                                {!isGlobal && (
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fixed Fee (NGN)</th>
                                )}
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                                <tr key={config.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">
                                        {config.service_type}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {editingId === config.id ? (
                                            <div className="flex items-center">
                                                {isGlobal && <span className="text-gray-500 mr-1">₦</span>}
                                                <input
                                                    type="number"
                                                    step={isGlobal ? "1" : "0.1"}
                                                    value={editForm.markup_percentage}
                                                    onChange={(e) => setEditForm({ ...editForm, markup_percentage: Number(e.target.value) })}
                                                    className="w-24 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                />
                                            </div>
                                        ) : (
                                            isGlobal ? `₦ ${config.markup_percentage.toLocaleString()}` : `${config.markup_percentage}%`
                                        )}
                                    </td>
                                    {!isGlobal && (
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {editingId === config.id ? (
                                                <div className="flex items-center">
                                                    <span className="text-gray-500 mr-1">₦</span>
                                                    <input
                                                        type="number"
                                                        value={editForm.fixed_fee}
                                                        onChange={(e) => setEditForm({ ...editForm, fixed_fee: Number(e.target.value) })}
                                                        className="w-32 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    />
                                                </div>
                                            ) : (
                                                `₦ ${config.fixed_fee.toLocaleString()}`
                                            )}
                                        </td>
                                    )}
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        {editingId === config.id ? (
                                            <div className="flex justify-end space-x-2">
                                                <button
                                                    onClick={() => handleSave(config.id)}
                                                    className="text-green-600 hover:text-green-900"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    onClick={handleCancel}
                                                    className="text-gray-600 hover:text-gray-900"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => handleEdit(config)}
                                                className="text-blue-600 hover:text-blue-900"
                                            >
                                                Edit
                                            </button>
                                        )}
                                    </td>
                                </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    if (loading && configs.length === 0) return <div className="p-6">Loading pricing configurations...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Platform Pricing & Conversion Controls</h2>
                {error && <div className="text-red-600 text-sm font-semibold bg-red-50 px-3 py-1 rounded-md">{error}</div>}
            </div>

            {renderCategorySection('Global Conversion Rate', 'global', 'ri-exchange-dollar-line', 'emerald')}
            {renderCategorySection('Flights Pricing', 'flight', 'ri-plane-line', 'green')}
            {renderCategorySection('Hotels Pricing', 'hotel', 'ri-hotel-line', 'blue')}
            {renderCategorySection('Transportation Pricing', 'taxi', 'ri-taxi-line', 'orange')}
            {renderCategorySection('Umrah Packages Pricing', 'umrah', 'ri-moon-clear-line', 'purple')}
            {renderCategorySection('Hajj Packages Pricing', 'hajj', 'ri-star-smile-line', 'indigo')}
        </div>
    );
};

export default PricingControl;
